import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, User, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { publicApi } from '../lib/api';
import SEO from '../components/SEO';

const ResourcesPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const res = await publicApi.getBlogPosts();
        setPosts(res.data);
      } catch (error) {
        console.error('Error loading posts:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPosts();
    publicApi.trackEvent({ event_type: 'page_view', page: 'resources' }).catch(() => {});
  }, []);

  const categories = ['all', ...new Set(posts.map(p => p.category))];
  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(p => p.category === selectedCategory);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div data-testid="resources-page">
      <SEO 
        title="Legal Resources & Blog"
        description="Expert legal insights on trademarks, business formation, contracts, and more. Stay informed with articles from Attorney Jennifer N. Brantley."
      />
      {/* Hero */}
      <section className="bg-[#FDFCFA] border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-3xl">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-6">
              Insights & Resources
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight leading-[1.1] text-[#222222] mb-6">
              Legal Resources
            </h1>
            <p className="text-lg text-[#555555] leading-relaxed">
              Practical insights and guidance on trademarks, business formation, contracts, and more. Stay informed about the legal issues that matter to your business.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-white border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 text-sm font-medium transition-colors rounded-sm ${
                  selectedCategory === category
                    ? 'bg-[#1F2A44] text-white'
                    : 'bg-[#F5F3EF] text-[#555555] hover:bg-[#E8E4DC]'
                }`}
                data-testid={`category-${category}`}
              >
                {category === 'all' ? 'All Topics' : category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-[#E8E4DC] h-48 mb-4"></div>
                  <div className="bg-[#E8E4DC] h-6 w-3/4 mb-2"></div>
                  <div className="bg-[#E8E4DC] h-4 w-full mb-2"></div>
                  <div className="bg-[#E8E4DC] h-4 w-2/3"></div>
                </div>
              ))}
            </div>
          ) : filteredPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <article
                  key={post.id}
                  className="group border border-[#E8E4DC] hover:border-[#1F2A44]/30 hover:shadow-xl transition-all duration-500"
                  data-testid={`blog-post-${index}`}
                >
                  <div className="bg-[#1F2A44] h-48 flex items-center justify-center p-6">
                    <h3 className="text-xl font-serif text-white text-center line-clamp-3">
                      {post.title}
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-xs text-[#555555] mb-4">
                      <span className="flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        {post.category}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(post.created_at)}
                      </span>
                    </div>
                    <p className="text-[#555555] text-sm leading-relaxed mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <Link
                      to={`/resources/${post.slug}`}
                      className="inline-flex items-center text-sm font-medium text-[#1F2A44] group-hover:gap-2 transition-all"
                    >
                      Read more
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity ml-1" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-[#555555] text-lg mb-6">
                {selectedCategory === 'all' 
                  ? 'No articles available yet. Check back soon!'
                  : `No articles in the "${selectedCategory}" category yet.`}
              </p>
              {selectedCategory !== 'all' && (
                <Button
                  onClick={() => setSelectedCategory('all')}
                  variant="outline"
                  className="border-[#1F2A44] text-[#1F2A44]"
                >
                  View All Articles
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#F5F3EF] border-t border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-[#222222] mb-6">
            Have Questions About Your Situation?
          </h2>
          <p className="text-lg text-[#555555] mb-8 max-w-2xl mx-auto">
            These resources provide general information, but every situation is unique. Book a consultation to discuss your specific needs.
          </p>
          <Link to="/book">
            <Button className="bg-[#1F2A44] hover:bg-[#151D30] text-white px-10 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
              Book a Consultation
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;
